/* eslint-disable complexity */
import { DateTime } from 'luxon';
import {
  BranchWhereInput,
  SpottedVehicleReportListCsv2Query,
  SpottedVehicleReportListQuery,
  SpottedVehicleReportWhereInput,
} from '@rapid-recovery-agency-inc/insightt-graphql';
import {
  Branch,
  CaseOrderType,
  CaseStatus,
  getFormattedDateWithTz,
  getOrderType,
  SECURED_STRING,
  UNASSIGNED_BRANCH_NAME,
} from '@rapid-recovery-agency-inc/insightt-shared';
import { getUserName } from '@rapid-recovery-agency-inc/sloth-ui-web';

import { AppState } from '../../redux/store';
import { ExportSpottedVehiclesReportInput } from './spotted-vehicles-types';

export function createSpottedVehiclesReportCsvWhere(
  filter: ExportSpottedVehiclesReportInput,
): SpottedVehicleReportWhereInput {
  const AND: SpottedVehicleReportWhereInput['AND'] = [];

  if (filter.startDate) {
    AND.push({
      spottedDate: {
        gte: filter.startDate,
      },
    });
  }

  if (filter.endDate) {
    AND.push({
      spottedDate: {
        lte: filter.endDate,
      },
    });
  }

  return {
    AND,
  };
}

export function sortCSVByColumn(csvArray: string[], column: number): string[] {
  const [header, ...newArray] = csvArray;

  return [header].concat(
    [...newArray].sort((a, b) => {
      const valueA = a.split(',')[column];
      const valueB = b.split(',')[column];

      // Compare the values
      if (valueA < valueB) {
        return -1;
      }
      if (valueA > valueB) {
        return 1;
      }
      return 0;
    }),
  );
}
export function sortCaseStatusColumn(csvArray: string[]): string[] {
  const [header, ...newArray] = csvArray;

  return [header].concat(
    [...newArray].sort((a, b) => {
      const valueA = a.split(', ')[5];
      const valueB = b.split(', ')[5];

      if (valueA === SECURED_STRING && valueB !== SECURED_STRING) {
        return -1; // 'secured' comes first
      } else if (valueA !== SECURED_STRING && valueB === SECURED_STRING) {
        return 1; // 'secured' comes first
      } else {
        return valueA.localeCompare(valueB);
      }
    }),
  );
}
export function mapSpottedVehiclesReportToCsvRow(
  records: SpottedVehicleReportListQuery['spottedVehicleReports'],
  timezone?: string,
): string[] {
  return records.map((record) => {
    const status = record.case?.status;
    const mappedStatus = record.case?.status === CaseStatus.REPOSSESSED ? SECURED_STRING : status;
    const user = record.user;

    return [
      getFormattedDateWithTz(record.spottedDate, timezone, 'MM/DD/YYYY'),
      getFormattedDateWithTz(record.spottedDate, timezone, 'hh:mm A'),
      record.case?.vin ?? 'Unknown',
      user.firstName || user.lastName ? `${user.firstName} ${user.lastName}` : 'Unknown',
      record.subBranch?.branch?.name ? record.subBranch?.branch?.name : UNASSIGNED_BRANCH_NAME,
      status ? mappedStatus : 'Unknown',
      record.case?.orderType
        ? getOrderType(record.case?.orderType as CaseOrderType) || record.case?.orderType
        : 'Unknown',
    ]
      .map((value) => (value ? value.replaceAll(',', '') : ''))
      .join(', ');
  });
}

export function mapSpottedVehiclesReportWithRecoveryDataToCsvRowV2(
  records: SpottedVehicleReportListCsv2Query['spottedVehicleReports'],
): string {
  const headers = [
    'Spotted Date',
    'Time', // spotted time
    'Recovery Date',
    'Time', // recovery time
    'VIN',
    'Spotted By',
    'Recovery Agent',
    'Branch',
    'Status',
    'Reach Status',
    'Order Type',
    // acts like a coma at the end of the line
    '',
  ]
    .join(', ')
    .concat('\n');

  const data = records
    .map((record) => {
      const status = record.case?.status;
      const mappedStatus = record.case?.status === CaseStatus.REPOSSESSED ? SECURED_STRING : status;
      const user = record.user;

      return [
        DateTime.fromISO(record.spottedDate).toLocaleString(DateTime.DATE_SHORT),
        DateTime.fromISO(record.spottedDate).toLocaleString(DateTime.TIME_SIMPLE),
        record.case.rdnRepossession?.rdnRepossessionDatetime
          ? DateTime.fromISO(record.case.rdnRepossession.rdnRepossessionDatetime).toLocaleString(
              DateTime.DATE_SHORT,
            )
          : 'N/A',
        record.case.rdnRepossession?.rdnRepossessionDatetime
          ? DateTime.fromISO(record.case.rdnRepossession.rdnRepossessionDatetime).toLocaleString(
              DateTime.TIME_SIMPLE,
            )
          : 'N/A',
        record.case?.vin ?? 'Unknown',
        user.firstName || user.lastName ? `${user.firstName} ${user.lastName}` : 'Unknown',
        record.case.rdnRepossession?.rdnRepossessionDatetime
          ? getUserName({ ...record.case.rdnRepossession.user, role: 'agent' })
          : 'N/A',
        record.subBranch?.branch?.name ? record.subBranch?.branch?.name : UNASSIGNED_BRANCH_NAME,
        status ? mappedStatus : 'Unknown',
        record.unitReachStatus ?? 'N/A',
        record.case?.orderType
          ? getOrderType(record.case?.orderType as CaseOrderType) || record.case?.orderType
          : 'Unknown',
      ]

        .map((value) => (value ? value.replaceAll(',', '') : ''))

        .join(', ');
    })
    .join('\n');

  return headers.concat(data);
}

export function getBranchesIdsFromStore(store: AppState): number[] {
  const branches = store.app.branches as Branch[];

  return branches.map((branch) => branch.id);
}

export function getBranchFilter(branch: number): BranchWhereInput | null | undefined {
  if (branch < 0) {
    return null;
  }
  if (branch === 0) {
    return undefined;
  }

  const where: BranchWhereInput = {
    id: {
      equals: branch,
    },
  };

  return where;
}
